import { create } from 'zustand'
import {
  Asset,
  AssetDetail,
  AssetPagination,
  CreateAssetRequest,
  UpdateAssetRequest,
  AssetStatus,
  AccountEmvBreakdown,
  HashtagEmvBreakdown,
  CategoryEmvBreakdown
} from '../types/asset'
import { assetService } from '../services/assetService'

interface AssetPerformanceData {
  totalEmv: number
  totalPosts: number
  totalEngagement: number
  totalLikes: number
  totalComments: number
  totalShares: number
  totalViews: number
  totalSaves: number
  accountBreakdown: AccountEmvBreakdown[]
  hashtagBreakdown: HashtagEmvBreakdown[]
  categoryBreakdown: CategoryEmvBreakdown[]
}

interface AssetState {
  // State
  assets: Asset[]
  pagination: AssetPagination | null
  loading: boolean
  error: string | null
  selectedAsset: AssetDetail | null

  // Actions
  loadAssets: (
    customerId: string,
    status?: AssetStatus,
    categoryId?: string,
    postTypeId?: string,
    search?: string,
    page?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ) => Promise<void>
  loadAsset: (id: string, customerId: string) => Promise<void>
  loadAssetPerformance: (id: string, customerId: string) => Promise<AssetPerformanceData | null>
  createAsset: (customerId: string, data: CreateAssetRequest) => Promise<Asset>
  updateAsset: (id: string, customerId: string, data: UpdateAssetRequest) => Promise<Asset>
  deleteAsset: (id: string, customerId: string) => Promise<void>
  setAssetHashtags: (id: string, customerId: string, hashtagIds: number[]) => Promise<Asset>
  removeAssetHashtag: (id: string, customerId: string, hashtagId: number) => Promise<void>
  setSelectedAsset: (asset: AssetDetail | null) => void
  reset: () => void
}

const useAssetStore = create<AssetState>((set) => ({
  // Initial State
  assets: [],
  pagination: null,
  loading: false,
  error: null,
  selectedAsset: null,

  // Actions
  loadAssets: async (
    customerId: string,
    status?: AssetStatus,
    categoryId?: string,
    postTypeId?: string,
    search?: string,
    page?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ) => {
    set({ loading: true, error: null })
    try {
      const { data, pagination } = await assetService.getAssets(
        customerId,
        status,
        categoryId,
        postTypeId,
        search,
        page,
        limit,
        sortBy,
        sortOrder
      )
      set({ assets: data, pagination, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load assets',
        loading: false
      })
    }
  },

  loadAsset: async (id: string, customerId: string) => {
    set({ loading: true, error: null })
    try {
      const asset = await assetService.getAsset(id, customerId)
      set({ selectedAsset: asset, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load asset',
        loading: false
      })
    }
  },

  loadAssetPerformance: async (id: string, customerId: string) => {
    try {
      const metrics = await assetService.getAssetPerformance(id, customerId)
      return {
        totalEmv: metrics.totalEmv || 0,
        totalPosts: metrics.totalPosts || 0,
        totalEngagement: metrics.totalEngagement || 0,
        totalLikes: metrics.totalLikes || 0,
        totalComments: metrics.totalComments || 0,
        totalShares: metrics.totalShares || 0,
        totalViews: metrics.totalViews || 0,
        totalSaves: metrics.totalSaves || 0,
        accountBreakdown: metrics.accountBreakdown || [],
        hashtagBreakdown: metrics.hashtagBreakdown || [],
        categoryBreakdown: metrics.categoryBreakdown || []
      }
    } catch {
      return null
    }
  },

  createAsset: async (customerId: string, data: CreateAssetRequest) => {
    const asset = await assetService.createAsset(customerId, data)
    // Note: We don't add to local state here because the API response may not include
    // the full nested objects (asset_categories, post_types, asset_hashtags) needed for display.
    // The AssetsList component will reload assets after successful creation.
    return asset
  },

  updateAsset: async (id: string, customerId: string, data: UpdateAssetRequest) => {
    const updated = await assetService.updateAsset(id, customerId, data)
    // Note: We don't update local state here because the API response may not include
    // the full nested objects (asset_categories, post_types, asset_hashtags) needed for display.
    // The EditAssetDrawer component will reload assets after successful update.
    return updated
  },

  setAssetHashtags: async (id: string, customerId: string, hashtagIds: number[]) => {
    const asset = await assetService.setAssetHashtags(id, customerId, hashtagIds)
    // Note: We don't update local state here because the API response may not include
    // the full nested objects needed for display.
    return asset
  },

  removeAssetHashtag: async (id: string, customerId: string, hashtagId: number) => {
    await assetService.removeAssetHashtag(id, customerId, hashtagId)
    // Note: We don't update local state here - caller should reload if needed
  },

  deleteAsset: async (id: string, customerId: string) => {
    await assetService.deleteAsset(id, customerId)

    // Remove from local state
    set((state) => ({
      assets: state.assets.filter((asset) => asset.id !== id),
      selectedAsset: state.selectedAsset?.id === id ? null : state.selectedAsset
    }))
  },

  setSelectedAsset: (asset: AssetDetail | null) => {
    set({ selectedAsset: asset })
  },

  reset: () =>
    set({
      assets: [],
      pagination: null,
      loading: false,
      error: null,
      selectedAsset: null
    })
}))

export default useAssetStore
